/**
 * Animation Management System - Moon Moon Agency v2.1
 * Smooth scrolling with Lenis, text splitting, and button animations
 */

/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const ANIMATION_CONFIG = {
	// Device Settings
	mobileBreakpoint: 768, // Consistent with other modules
	
	// Lenis Settings
	lenis: {
		duration: 0.85,
		easing: (t) => Math.min(1, 1.001 * (1 - Math.pow(2, -10 * t))),
		orientation: 'vertical',
		gestureOrientation: 'vertical',
		smoothWheel: true,
		wheelMultiplier: 1,
		// Désactivé : le scroll natif iOS/Android est plus fluide, smoothTouch
		// génère du jank sur de nombreux devices.
		smoothTouch: false,
		touchMultiplier: 1.5,
		infinite: false,
		autoResize: true,
		prevent: (node) => {
			return node.closest('[data-lenis-prevent]') ||
					node.hasAttribute('data-lenis-prevent');
		}
	},
	
	// Animation Timings
	timings: {
		buttonAnimation: 750,
		lenisDebounce: 200,
		rafTimeout: 100
	},
	
	// Selectors - Using data attributes
	selectors: {
		splitText: '[data-split-text]',
		stopLenis: '[data-stop-lenis]',
		preventLenis: '[data-lenis-prevent]',
		animatedButtons: '[data-animated-btn]',
		htmlElement: 'html'
	},

	// Development mode (lazy evaluation)
	get isDevelopment() {
		return typeof isDevelopment !== 'undefined' ? isDevelopment : false;
	}
};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */
// Logging system
const animationLog = (level, message, data = {}) => {
	if (!ANIMATION_CONFIG.isDevelopment && level === 'info') return;
	if (typeof console !== 'undefined') {
		const logFn = console[level] || console.log;
		logFn(`✨ [ANIMATION] ${message}`, { timestamp: window.getTimestamp?.() || new Date().toLocaleString(), ...data });
	}
};

// Animation Event Emitter
const animationEmit = (event, detail = {}, cancelable = false) => {
	const eventData = {
		timestamp: window.getTimestamp?.() || new Date().toLocaleString(),
		event,
		data: detail,
		source: 'animation'
	};
	
	return document.dispatchEvent(new CustomEvent(`animation:${event}`, {
		detail: eventData,
		cancelable,
		bubbles: true
	}));
};

// Device Detection
const DeviceManager = {
	get isMobile() {
		return window.innerWidth < ANIMATION_CONFIG.mobileBreakpoint;
	},
	
	get isDesktop() {
		return !this.isMobile;
	}
};

// DOM Selectors Cache
const AnimationDOM = {
	get html() { return document.querySelector(ANIMATION_CONFIG.selectors.htmlElement); },
	get splitTextElements() { return document.querySelectorAll(ANIMATION_CONFIG.selectors.splitText); },
	get stopLenisElements() { return document.querySelectorAll(ANIMATION_CONFIG.selectors.stopLenis); },
	get animatedButtons() { return document.querySelectorAll(ANIMATION_CONFIG.selectors.animatedButtons); }
};

/*********************************************************************************************
 * 🔌 EXTERNAL LIBRARIES MANAGER
 */
const LibraryManager = {
	checkDependencies() {
		const dependencies = [
			{ name: 'GSAP', check: () => typeof gsap !== 'undefined' },
			{ name: 'ScrollTrigger', check: () => typeof ScrollTrigger !== 'undefined' },
			{ name: 'CustomEase', check: () => typeof CustomEase !== 'undefined' },
			{ name: 'Lenis', check: () => typeof Lenis !== 'undefined' },
			{ name: 'SplitType', check: () => typeof SplitType !== 'undefined' }
		];
		
		const missing = dependencies.filter(dep => !dep.check());
		
		if (missing.length > 0) {
			animationLog('warn', 'Missing animation dependencies', { 
				missing: missing.map(dep => dep.name),
				available: dependencies.filter(dep => dep.check()).map(dep => dep.name)
			});
			return false;
		}
		
		return true;
	},
	
	initGSAP() {
		try {
			if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined' && typeof CustomEase !== 'undefined') {
				gsap.registerPlugin(ScrollTrigger, CustomEase);
				animationEmit('gsap-initialized');
				return true;
			}
		} catch (error) {
			animationLog('error', 'Failed to initialize GSAP', { error: error.message });
		}
		return false;
	}
};

/*********************************************************************************************
 * ✂️ TEXT SPLITTING MANAGER
 */
const TextSplitter = {
	init() {
		if (DeviceManager.isMobile || typeof SplitType === 'undefined') {
			animationLog('info', 'Text splitting skipped', { 
				reason: DeviceManager.isMobile ? 'mobile device' : 'SplitType not available'
			});
			return;
		}
		
		try {
			const elements = AnimationDOM.splitTextElements;
			animationLog('info', 'Initializing text splitting', { elementCount: elements.length });
			
			elements.forEach((element, index) => {
				try {
					const splitResult = SplitType.create(element, {
						types: "chars",
						tagName: "div",
					});

					// Apply --char-index to each character for CSS animations
					if (splitResult.chars) {
						splitResult.chars.forEach((char, charIndex) => {
							char.style.setProperty("--char-index", charIndex);
						});
						
						animationEmit('text-split-success', { 
							elementIndex: index,
							charsCount: splitResult.chars.length 
						});
					}
				} catch (error) {
					animationLog('warn', 'Failed to split text element', { 
						elementIndex: index,
						error: error.message 
					});
				}
			});
			
			animationEmit('text-splitting-complete', { processedElements: elements.length });
		} catch (error) {
			animationLog('error', 'Text splitting initialization failed', { error: error.message });
		}
	}
};

/*********************************************************************************************
 * 🔄 LENIS SMOOTH SCROLL MANAGER
 */
const LenisManager = {
	instance: null,
	rafId: null,
	shouldBeStopped: false,
	observer: null,
	debounceTimeout: null,
	
	init() {
		if (typeof Lenis === 'undefined') {
			animationLog('info', 'Lenis initialization skipped', { 
				reason: 'Lenis not available'
			});
			return false;
		}
		
		try {
			this.start();
			this.setupStopElements();
			this.setupMutationObserver();
			this.setupAnchorLinks();
			animationEmit('lenis-initialized');
			return true;
		} catch (error) {
			animationLog('error', 'Lenis initialization failed', { error: error.message });
			return false;
		}
	},
	
	start() {
		if (this.shouldBeStopped) return;
		if (DeviceManager.isMobile) return; // Native scroll on iOS/Android is smoother than Lenis + 60fps RAF

		try {
			// Clean up existing instance
			if (this.instance) {
				this.destroy();
			}
			
			this.instance = new Lenis(ANIMATION_CONFIG.lenis);
			
			// Sync ScrollTrigger
			this.instance.on('scroll', () => {
				if (typeof ScrollTrigger !== 'undefined') {
					ScrollTrigger.update();
				}
			});

			// Sync with GSAP ticker for better performance and sync
			if (typeof gsap !== 'undefined') {
				gsap.ticker.add((time) => {
					this.instance.raf(time * 1000);
				});
				gsap.ticker.lagSmoothing(0);
			} else {
				this.startRAF();
			}
			
			if (AnimationDOM.html) {
				AnimationDOM.html.classList.add('lenis-active');
				AnimationDOM.html.classList.remove("stop-scrolling");
			}
			
			animationEmit('lenis-started');
			animationLog('info', 'Lenis smooth scrolling started');
		} catch (error) {
			animationLog('error', 'Failed to start Lenis', { error: error.message });
		}
	},
	
	stop() {
		if (!this.instance && !DeviceManager.isMobile) return;
		
		try {
			this.shouldBeStopped = true;
			if (this.instance) {
				this.instance.stop();
			}
			
			// On bloque TOUJOURS sur le body pour éviter de casser les éléments sticky
			document.body.classList.add("stop-scrolling");
			document.body.classList.add("mm-stop-scrolling");
			
			animationEmit('lenis-stopped');
			animationLog('info', 'Lenis smooth scrolling stopped');
		} catch (error) {
			animationLog('error', 'Failed to stop Lenis', { error: error.message });
		}
	},

	resume() {
		if (!this.instance && !DeviceManager.isMobile) return;
		
		try {
			this.shouldBeStopped = false;
			if (this.instance) {
				this.instance.start();
			}
			
			// On débloque le body
			document.body.classList.remove("stop-scrolling");
			document.body.classList.remove("mm-stop-scrolling");
			
			animationEmit('lenis-resumed');
			animationLog('info', 'Lenis smooth scrolling resumed');
		} catch (error) {
			animationLog('error', 'Failed to resume Lenis', { error: error.message });
		}
	},
	
	destroy() {
		if (this.instance) {
			try {
				this.instance.destroy();
				this.instance = null;
			} catch (error) {
				animationLog('warn', 'Error destroying Lenis instance', { error: error.message });
			}
		}
		
		if (this.rafId) {
			cancelAnimationFrame(this.rafId);
			this.rafId = null;
		}
	},
	
	startRAF() {
		if (!this.instance) return;
		
		const raf = (time) => {
			if (this.instance && !this.shouldBeStopped) {
				try {
					this.instance.raf(time);
					this.rafId = requestAnimationFrame(raf);
				} catch (error) {
					animationLog('warn', 'RAF error, stopping animation loop', { error: error.message });
					this.rafId = null;
				}
			}
		};
		
		this.rafId = requestAnimationFrame(raf);
	},
	
	setupStopElements() {
		const elements = AnimationDOM.stopLenisElements;
		animationLog('info', 'Setting up Lenis stop elements', { elementCount: elements.length });
		
		elements.forEach((element) => {
			element.addEventListener('mouseenter', () => this.stop(), { passive: true });
			element.addEventListener('mouseleave', () => this.resume(), { passive: true });
		});
	},
	
	setupAnchorLinks() {
		document.querySelectorAll('a[href^=\"#\"]').forEach((anchor) => {
			anchor.addEventListener('click', (e) => {
				const href = anchor.getAttribute('href');
				if (href === '#' || !href.startsWith('#')) return;

				const target = document.querySelector(href);
				if (target) {
					// Mobile : smooth scroll natif avec offset header
					if (DeviceManager.isMobile) {
						e.preventDefault();
						const styles = getComputedStyle(document.documentElement);
						const headerHeight = parseFloat(styles.getPropertyValue('--header-height-mobile')) || 54;
						const announcementHeight = parseFloat(styles.getPropertyValue('--announcement-bar-height-mobile')) || 33;
						const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - announcementHeight;
						window.scrollTo({ top, behavior: 'smooth' });
						if (history.pushState) {
							history.pushState(null, null, href);
						} else {
							location.hash = href;
						}
						return;
					}

					e.preventDefault();

					// Desktop : Lenis ignore scroll-padding-top, on passe l'offset manuellement
					const styles = getComputedStyle(document.documentElement);
					const headerHeight = parseFloat(styles.getPropertyValue('--header-height')) || 66;
					const announcementHeight = parseFloat(styles.getPropertyValue('--announcement-bar-height')) || 33;
					this.scrollTo(target, { offset: -(headerHeight + announcementHeight) });

					// Update URL hash without jumping
					if (history.pushState) {
						history.pushState(null, null, href);
					} else {
						location.hash = href;
					}
				}
			});
		});
	},
	
	setupMutationObserver() {
		if (typeof MutationObserver === 'undefined') return;
		if (DeviceManager.isMobile) return; // No Lenis on mobile, resize() would be a no-op anyway

		try {
			this.observer = new MutationObserver(() => {
				// Debounce to avoid restarting Lenis too often
				if (this.debounceTimeout) {
					clearTimeout(this.debounceTimeout);
				}
				
				this.debounceTimeout = setTimeout(() => {
					if (!this.shouldBeStopped && DeviceManager.isDesktop) {
						this.resize();
					}
				}, ANIMATION_CONFIG.timings.lenisDebounce);
			});

			this.observer.observe(document.body, { 
				childList: true, 
				subtree: true 
			});
			
		} catch (error) {
			animationLog('warn', 'Failed to setup mutation observer', { error: error.message });
		}
	},
	
	scrollTo(target, options = {}) {
		if (DeviceManager.isMobile || !this.instance) return;
		
		try {
			this.instance.scrollTo(target, options);
			animationEmit('lenis-scroll-to', { target, options });
		} catch (error) {
			animationLog('error', 'Failed to scroll to target', { error: error.message, target });
		}
	},

	resize() {
		if (DeviceManager.isMobile || !this.instance) return;
		
		try {
			this.instance.resize();
			animationEmit('lenis-resized');
		} catch (error) {
			animationLog('error', 'Failed to resize Lenis', { error: error.message });
		}
	},

	cleanup() {
		this.destroy();
		
		if (this.observer) {
			this.observer.disconnect();
			this.observer = null;
		}
		
		if (this.debounceTimeout) {
			clearTimeout(this.debounceTimeout);
			this.debounceTimeout = null;
		}
		
		animationEmit('lenis-cleanup-complete');
	}
};

/*********************************************************************************************
 * 🎯 BUTTON ANIMATIONS MANAGER
 */
const ButtonAnimations = {
	init() {
		const buttons = AnimationDOM.animatedButtons;
		animationLog('info', 'Initializing button animations', { buttonCount: buttons.length });
		
		buttons.forEach((button) => {
			// Remove existing listeners to avoid duplicates
			button.removeEventListener('click', this.handleClick);
			button.addEventListener('click', this.handleClick.bind(this), { passive: true });
		});
		
		animationEmit('button-animations-initialized', { buttonCount: buttons.length });
	},
	
	handleClick(event) {
		const button = event.currentTarget;
		
		try {
			button.classList.add('clicked');
			
			animationEmit('button-clicked', { 
				buttonId: button.id || 'no-id',
				buttonClass: button.className 
			});
			
			// Remove class after animation completes
			setTimeout(() => {
				button.classList.remove('clicked');
			}, ANIMATION_CONFIG.timings.buttonAnimation);
			
		} catch (error) {
			animationLog('warn', 'Button animation failed', { error: error.message });
		}
	}
};

/*********************************************************************************************
 * 🏔️ HERO PARALLAX MANAGER
 *********************************************************************************************/
const HeroParallax = {
	init() {
		if (DeviceManager.isMobile) return; // ScrollTrigger scrub on a playing <video> destroys iOS scroll perf

		// On garde GSAP + ScrollTrigger pour gérer le scroll
		if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
			animationLog('info', 'Hero parallax skipped', {
				reason: 'GSAP/ScrollTrigger not available'
			});
			return;
		}

		const wrappers = document.querySelectorAll('.mm-hero-media[data-parallax=\"true\"]');
		if (!wrappers.length) {
			return;
		}

		animationLog('info', 'Initializing hero parallax', {
			count: wrappers.length
		});

		wrappers.forEach((wrapper, index) => {
			try {
				// On cible le media à l’intérieur (vidéo ou image/picture)
				const media = wrapper.querySelector('video, img, picture');
				if (!media) return;

				const direction = wrapper.dataset.parallaxDirection || 'up';
				const speedValue = parseFloat(wrapper.dataset.parallaxSpeed || '3');

				// Mapping simple : 1 → 10%, 5 → 30%
				const clampedSpeed = Math.min(Math.max(speedValue, 1), 5);
				const baseOffset = 4;
				const extraPerStep = 2;
				const maxOffset = baseOffset + (clampedSpeed - 1) * extraPerStep;

				const fromY = direction === 'down' ? -maxOffset : maxOffset;
				const toY = direction === 'down' ? maxOffset : -maxOffset;

				// Animation GSAP + ScrollTrigger
				gsap.fromTo(
					media,
					{ yPercent: fromY },
					{
						yPercent: toY,
						ease: 'none',
						scrollTrigger: {
							trigger: wrapper,
							start: 'top bottom',
							end: 'bottom top',
							scrub: true
						}
					}
				);

				animationEmit('hero-parallax-init', {
					index,
					direction,
					speed: clampedSpeed,
					maxOffset
				});
			} catch (error) {
				animationLog('warn', 'Hero parallax setup failed', {
					index,
					error: error.message
				});
			}
		});
	}
};

/*********************************************************************************************
 * 🧭 CATEGORIE LISTE (MM-Categorie-Liste)
 * - Règle verticale qui suit la souris
 * - Changement d'image au survol d'une catégorie
 *********************************************************************************************/
const CategorieListeManager = {
	init() {
		if (typeof document === 'undefined') return;
		if (typeof gsap === 'undefined') {
			animationLog('info', 'CategorieListeManager skipped', { reason: 'GSAP not available' });
			return;
		}

		const sections = document.querySelectorAll('[data-section-id-categorie-liste]');
		if (!sections.length) return;

		animationLog('info', 'Initializing CategorieListeManager', { sectionCount: sections.length });

		sections.forEach((section) => {
			try {
				const railLine = section.querySelector('[data-mm-categorie-rail-line]');
				const items = Array.from(section.querySelectorAll('[data-mm-categorie-item]'));
				const medias = Array.from(section.querySelectorAll('[data-mm-categorie-media]'));

				if (!railLine || !items.length || !medias.length) return;

				/* ------------------------------
				 * 1. Règle verticale interactive (ticks)
				 * ------------------------------ */
				const ticks = [];
				const lerpColor = (t) => {
					const v = Math.round(222 + (31 - 222) * Math.min(Math.max(t, 0), 1)); // #DEDEDE → #1F1F1F
					return `rgb(${v}, ${v}, ${v})`;
				};

				const buildTicks = () => {
					ticks.length = 0;
					railLine.innerHTML = '';
					const h = railLine.getBoundingClientRect().height || 260;
					const count = Math.floor(h / 12);
					if (!count) return;

					for (let i = 0; i < count; i++) {
						const tick = document.createElement('span');
						tick.className = 'mm-categorie-liste-rail-tick';
						tick.style.top = `${i * 12}px`;
						railLine.appendChild(tick);
						ticks.push({ el: tick, ratio: (i * 12 + 6) / h });
					}
				};

				const updateTicks = (clientY) => {
					const r = railLine.getBoundingClientRect();
					if (!r.height || !ticks.length) return;
					const y = Math.min(Math.max((clientY - r.top) / r.height, 0), 1);

					// Rayon d'influence = 6 ticks de chaque côté (12 ticks au total)
					// Distance entre ticks en ratio : 12px / hauteur totale
					const tickSpacingRatio = 12 / r.height;
					const influenceRadius = 4 * tickSpacingRatio; // 4 ticks de chaque côté

					ticks.forEach(({ el, ratio }) => {
						const strength = Math.max(0, 1 - Math.abs(ratio - y) / influenceRadius);
						gsap.to(el, {
							width: 10 + 25 * strength, // 10px (petit) → 35px (grand)
							opacity: 0.7 + 0.3 * strength,
							backgroundColor: lerpColor(strength), // #DEDEDE → #1F1F1F
							duration: 0.25,
							ease: 'power2.out'
						});
					});
				};

				buildTicks();
				section.addEventListener('mousemove', (e) => updateTicks(e.clientY));
				const r = railLine.getBoundingClientRect();
				updateTicks(r.top + r.height / 2);
				window.addEventListener('resize', () => {
					buildTicks();
					const r = railLine.getBoundingClientRect();
					updateTicks(r.top + r.height / 2);
				});

				/* ------------------------------
				 * 2. Gestion actif (hover = actif persistant)
				 * ------------------------------ */
				const setActive = (index) => {
					items.forEach((item) => {
						const active = item.dataset.mmCategorieIndex === index;
						item.classList.toggle('mm-categorie-liste--active', active);
						item.classList.toggle('mm-categorie-liste-link--active', active);
					});
					medias.forEach((media) => {
						media.classList.toggle('is-active', media.dataset.mmCategorieIndex === index);
					});
				};

				const firstItem = items.find((item) => item.classList.contains('mm-categorie-liste--active')) || items[0];
				if (firstItem) setActive(firstItem.dataset.mmCategorieIndex);

				items.forEach((item) => {
					const activate = () => setActive(item.dataset.mmCategorieIndex);
					['mouseenter', 'focus', 'click'].forEach((evt) => item.addEventListener(evt, activate));
				});
			} catch (error) {
				animationLog('warn', 'CategorieListeManager setup failed', { error: error.message });
			}
		});
	}
};

/*********************************************************************************************
 * 🎬 MAIN ANIMATION MANAGER
 */
const AnimationManager = {
	initialized: false,
	
	init() {
		if (this.initialized) return;
		
		try {
			animationEmit('init-start');
			animationLog('info', 'Initializing animation system');
			
			// Check dependencies first
			if (!LibraryManager.checkDependencies()) {
				animationLog('warn', 'Animation system running with limited functionality');
			}
			
			// 1. Initialiser GSAP (VITAL)
			LibraryManager.initGSAP();
			
			// 2. Initialiser Lenis (PRIORITÉ HAUTE)
			try {
				LenisManager.init();
			} catch (e) {
				animationLog('error', 'Lenis initialization failed', { error: e.message });
			}
			
			// 3. Initialiser les autres modules (Indépendant les uns des autres)
			const modules = [
				{ name: 'TextSplitter', init: () => TextSplitter.init() },
				{ name: 'ButtonAnimations', init: () => ButtonAnimations.init() },
				{ name: 'HeroParallax', init: () => HeroParallax.init() },
				{ name: 'CategorieListeManager', init: () => CategorieListeManager.init() }
			];

			modules.forEach(module => {
				try {
					module.init();
				} catch (e) {
					animationLog('warn', `${module.name} initialization failed`, { error: e.message });
				}
			});
			
			this.initialized = true;
			animationEmit('init-success');
			animationLog('info', 'Animation system fully initialized');
			
		} catch (error) {
			animationEmit('init-error', { error: error.message });
			animationLog('error', 'Animation system initialization failed', { error: error.message });
		}
	},
	
	cleanup() {
		try {
			// Lenis désactivé
			// LenisManager.cleanup();
			this.initialized = false;
			animationEmit('cleanup-complete');
		} catch (error) {
			animationLog('error', 'Animation cleanup failed', { error: error.message });
		}
	}
};

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */
const initAnimationManager = () => {
	if (!AnimationManager.initialized) {
		AnimationManager.init();
	}
};

// Cas 1 : le script est chargé avant DOMContentLoaded
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initAnimationManager);
} else {
	// Cas 2 : le script est injecté dynamiquement après DOMContentLoaded (lazy-load)
	initAnimationManager();
}

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
	AnimationManager.cleanup();
});

// Export to window for other modules
window.LenisManager = LenisManager;
window.AnimationManager = AnimationManager;

/*********************************************************************************************
 * 🔧 DEVELOPMENT LOGGING
 */
if (ANIMATION_CONFIG.isDevelopment) {
	['init-success'].forEach(event => {
		document.addEventListener(`animation:${event}`, e => 
			console.log(`✨ [ANIMATION] ${event}:`, e.detail)
		);
	});
}
